import {Checkbox, Dialog, MenuItem, Select, TextField} from "@mui/material";
import {ReactNode, useMemo, useState} from "react";
import {
    DialogActions,
    DialogContent,
    MultipleLineError,
    FormattedFormRow,
    FormContainer,
    FormDialogActionButtons,
    NumberField,
    FormattedDialogTitle,
    LoadingIcon,
} from "@dvrt/graphql-widgets";
import CreateAnotherCheckBox from "../../CreateAnotherCheckBox";
import {useForm} from "@/utils/Form";
import {useCreateCallGroupMutation} from "../queries.generated";
import ErrorHelper from "../../../utils/ErrorHelper";
import {showNotification} from "@/actions";
import {Link} from "../../widgets";
import {CreateCallGroupMutation, CreateCallGroupMutationVariables} from "../queries.generated";
import {CallGroupType} from "@/api";
import {FormattedMessage, MessageDescriptor, defineMessage} from "react-intl";
import {AVAILABLE_GROUP_SIZES, GroupSize} from "../types";
import QuotaItemSelector from "@/components/QuotaItemSelector";
import {limitInfoToQuotaItem, useCallGroupLimitsQuery} from "../tools";
import {CALL_GROUP_TYPE_MESSAGES} from "@/utils/translations";
import {QuotaInfo} from "@/components/EndpointCreateDialog";

export type GroupLimitInfo = {
    [size in GroupSize]: QuotaInfo;
};

type ContentFormProps = {
    domainId: number;
    onSuccess: () => void;
    close: () => void;
    successItemPath: string;
    limitInfo: GroupLimitInfo;
};

export type Props = {
    isOpen: boolean;
    title: MessageDescriptor;
    domainId: number;
    onSuccess: () => void;
    close: () => void;
    successItemPath: string;
};

const getFirstSelectableSize = (limitInfo: GroupLimitInfo) => {
    if (!limitInfo) return AVAILABLE_GROUP_SIZES[0];
    for (const size of AVAILABLE_GROUP_SIZES) {
        if (!limitInfo[size].allocationIncrementError) return size;
    }
    return AVAILABLE_GROUP_SIZES[0];
};

const DialogForm = ({domainId, onSuccess, close, limitInfo, successItemPath}: ContentFormProps) => {
    const form = useForm<CreateCallGroupMutation, CreateCallGroupMutationVariables>(
        useCreateCallGroupMutation,
        "createCallGroup",
        {
            domainId,
            name: "",
            type: CallGroupType.Serial,
            shortNumber: "",
            e164: null,
            sequentialTimeout: 10,
            pickup: false,
            size: getFirstSelectableSize(limitInfo),
        },
        (_form) =>
            showNotification(
                defineMessage({
                    id: "call_group_created",
                    defaultMessage: "Call group {link} has been successfully created.",
                }),
                "success",
                {
                    link: (
                        <Link
                            to={`/domains/${domainId}/${successItemPath}/${_form.result.data.createCallGroup.id}/settings`}
                        >
                            {_form.values.name}
                        </Link>
                    ),
                },
                10000,
            ),
    );
    const errorHelper = new ErrorHelper(form.errors);
    const [createAnother, setCreateAnother] = useState(false);

    const quotaItems = useMemo(() => {
        if (!limitInfo) return [form.values.size];
        return Object.entries(limitInfo ?? []).map(([key, value]) => limitInfoToQuotaItem(parseInt(key), value));
    }, [form.values.size, limitInfo]);

    function handleClose() {
        close();
        form.reset();
    }

    async function submit() {
        if (!(await form.submit())) return;
        onSuccess();

        if (createAnother) form.reset();
        else handleClose();
    }

    return (
        <>
            <DialogContent>
                <FormContainer>
                    <FormattedFormRow label={defineMessage({id: "groupSize", defaultMessage: "Group size"})} required>
                        <QuotaItemSelector
                            value={form.values.size}
                            updateValue={(size) => {
                                form.updateValues({size});
                            }}
                            items={quotaItems}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "type", defaultMessage: "Type"})} required>
                        <Select
                            variant="standard"
                            margin="dense"
                            value={form.values.type}
                            onChange={(event) => form.updateValues({type: event.target.value as CallGroupType})}
                        >
                            {([CallGroupType.Serial, CallGroupType.Parallel] as const).map((item) => (
                                <MenuItem value={item} key={item}>
                                    <FormattedMessage {...CALL_GROUP_TYPE_MESSAGES[item]} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.name}
                            onChange={(event) => form.updateValues({name: event.target.value})}
                            error={errorHelper.hasError("name")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("name")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "e164", defaultMessage: "E.164"})}>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.e164 || ""}
                            onChange={(event) => form.updateValues({e164: event.target.value || null})}
                            error={errorHelper.hasError("e164")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("e164")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "identifier", defaultMessage: "Identifier"})} required>
                        <TextField
                            variant="standard"
                            margin="dense"
                            value={form.values.shortNumber}
                            onChange={(event) => form.updateValues({shortNumber: event.target.value})}
                            error={errorHelper.hasError("shortNumber")}
                            helperText={<MultipleLineError errors={errorHelper.fieldErrors("shortNumber")} />}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow
                        label={defineMessage({id: "sequentialTimeout", defaultMessage: "Ringing timeout (seconds)"})}
                        required
                    >
                        <NumberField
                            variant="standard"
                            margin="dense"
                            value={form.values.sequentialTimeout}
                            max={90}
                            min={0}
                            update={(newValue: number) => {
                                form.updateValues({sequentialTimeout: newValue});
                            }}
                            errors={errorHelper.fieldErrors("sequentialTimeout")}
                        />
                    </FormattedFormRow>
                    <FormattedFormRow label={defineMessage({id: "pickup", defaultMessage: "Pickup group"})}>
                        <Checkbox
                            value={form.values.pickup}
                            onChange={(event) => form.updateValues({pickup: event.target.checked})}
                            color="primary"
                        />
                    </FormattedFormRow>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <CreateAnotherCheckBox state={createAnother} setState={setCreateAnother} />
                <FormDialogActionButtons
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    cancel={close}
                    enabled={form.hasChanged}
                />
            </DialogActions>
        </>
    );
};

const LoadingWrapper = ({loading, children}: {loading: boolean; children: ReactNode}) => {
    return loading ? <LoadingIcon /> : children;
};

const CreateCallGroupDialog = ({domainId, onSuccess, isOpen, close, title, successItemPath}: Props) => {
    const groupLimitInfo = useCallGroupLimitsQuery({variables: {domainId}, skip: !isOpen, fetchPolicy: "no-cache"});

    return (
        <Dialog open={isOpen} maxWidth="md">
            <FormattedDialogTitle title={title} />
            <LoadingWrapper loading={groupLimitInfo.loading}>
                <DialogForm
                    domainId={domainId}
                    onSuccess={onSuccess}
                    close={close}
                    limitInfo={groupLimitInfo.data}
                    successItemPath={successItemPath}
                />
            </LoadingWrapper>
        </Dialog>
    );
};

export default CreateCallGroupDialog;
